import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, type ArtifactPublicView, type Visibility } from "../lib/api";
import { ShareModal } from "./ShareModal";

interface Props {
  /**
   * The artifact whose actions this menu controls. Caller renders it only
   * for owners and editors; owner-only actions are gated on
   * ``artifact.access`` inside.
   */
  artifact: ArtifactPublicView;
  /** Optimistic-friendly callback after any mutation completes. */
  onChange?: () => void;
}

const VISIBILITY_LABEL: Record<Visibility, string> = {
  private: "Private",
  unlisted: "Unlisted",
  shared: "Shared",
};

/**
 * Popover menu triggered by the ⋯ button on an artifact card.
 *
 * Owners get every management action (Share, visibility selector, Versions,
 * Renew TTL, Make permanent / temporary, Delete). Editors get only their
 * slice — Share… (read-only lists) and Versions — because everything else is
 * owner-only server-side and would 403. Closes on outside-click and Escape.
 */
export function ArtifactCardMenu({ artifact, onChange }: Props) {
  const isOwner = artifact.access === "owner";
  const [open, setOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [busy, setBusy] = useState<"" | "vis" | "renew" | "perm" | "delete">(
    "",
  );
  const [visibility, setVisibility] = useState<Visibility>(artifact.visibility);
  const [permanent, setPermanent] = useState<boolean>(artifact.permanent);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on outside-click + Escape.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function changeVisibility(next: Visibility) {
    if (next === visibility) return;
    setBusy("vis");
    try {
      const updated = await api.setVisibility(artifact.slug, next);
      setVisibility(updated.visibility);
      onChange?.();
    } finally {
      setBusy("");
    }
  }
  async function renew() {
    setBusy("renew");
    try {
      await api.renewArtifact(artifact.slug);
      onChange?.();
    } finally {
      setBusy("");
    }
  }
  async function togglePermanent() {
    setBusy("perm");
    try {
      const updated = await api.setPermanent(artifact.slug, !permanent);
      setPermanent(updated.permanent);
      onChange?.();
    } finally {
      setBusy("");
    }
  }
  async function deleteArtifact() {
    if (!confirm(`Soft-delete "${artifact.title}"?`)) return;
    setBusy("delete");
    try {
      await api.deleteArtifact(artifact.slug);
      setOpen(false);
      onChange?.();
    } finally {
      setBusy("");
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        className={`rounded-md border px-2 py-1 text-sm leading-none ${
          open
            ? "border-slate-900 bg-slate-900 text-white"
            : "border-slate-200 bg-white text-slate-600 hover:border-slate-400 hover:text-slate-900"
        }`}
      >
        ⋯
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-20 mt-1 w-56 rounded-lg border border-slate-200 bg-white text-sm shadow-lg"
        >
          {/* Per-person sharing */}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              setShareOpen(true);
            }}
            className="block w-full border-b border-slate-100 px-3 py-2 text-left text-slate-700 hover:bg-slate-50"
          >
            Share…
          </button>

          {/* Visibility selector (owner-only server-side) */}
          {isOwner && (
            <div className="border-b border-slate-100 px-3 py-2">
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Visibility
              </div>
              <div className="flex gap-1">
                {(["private", "unlisted", "shared"] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => changeVisibility(v)}
                    disabled={busy === "vis"}
                    className={`flex-1 rounded border px-1.5 py-1 text-xs ${
                      visibility === v
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 text-slate-600 hover:border-slate-400"
                    } disabled:opacity-50`}
                  >
                    {VISIBILITY_LABEL[v]}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Actions — Versions is owner-or-editor; the rest owner-only */}
          <Link
            role="menuitem"
            to={`/artifacts/${artifact.slug}/versions`}
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-slate-700 hover:bg-slate-50"
          >
            Versions
          </Link>
          {isOwner && (
            <>
              <button
                type="button"
                role="menuitem"
                onClick={renew}
                disabled={busy === "renew"}
                className="block w-full px-3 py-2 text-left text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {busy === "renew" ? "Renewing…" : "Renew TTL"}
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={togglePermanent}
                disabled={busy === "perm"}
                title={
                  permanent
                    ? "Allow this artifact to expire on its TTL again"
                    : "Pin so the daily purge sweep skips this artifact"
                }
                className="block w-full px-3 py-2 text-left text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {busy === "perm"
                  ? "Saving…"
                  : permanent
                    ? "Make temporary"
                    : "Make permanent"}
              </button>
              <div className="border-t border-slate-100" />
              <button
                type="button"
                role="menuitem"
                onClick={deleteArtifact}
                disabled={busy === "delete"}
                className="block w-full px-3 py-2 text-left text-rose-700 hover:bg-rose-50 disabled:opacity-50"
              >
                {busy === "delete" ? "Deleting…" : "Delete…"}
              </button>
            </>
          )}
        </div>
      )}
      <ShareModal
        slug={artifact.slug}
        access={artifact.access}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        onChange={onChange}
      />
    </div>
  );
}
